import React, { useContext, useEffect, useRef, useState } from 'react'
import { ActionButton, ButtonModel, Pager } from '@notino/react-styleguide'
import { useIntl } from 'react-intl'

// types
import { PastReservationsResponse, MyReservationsPageQuery, MyReservationsPageSetQuery } from '../../../../types/types'

// utils
import { PAGE_LINKS } from '../../../../utils/helper'
import { AppContext } from '../../../../utils/appProvider'
import { PAST_RESERVATIONS_LIMIT_MY_RESERVATIONS_PAGE } from '../../../../utils/enums'
import { MyLocationContext } from '../../../../utils/myLocationProvider'

// components
import ErrorScreen from '../../../../atoms/ErrorScreen/ErrorScreen'
import Empty from '../../../../atoms/Empty/Empty'
import ReservationCard, { ReservationCardSkeleton } from '../../../../components/ReservationCard/ReservationCard'

// assets
import EmptyCalendarIcon from '../../../../assets/icons/EmptyCalendarIcon'

// hooks
import useMessages from '../../../../hooks/useMessages'

// styles
import * as SC from './PastReservationsStyles'

type Props = {
	pastReservationsContainerRef: React.RefObject<HTMLDivElement>
	query: MyReservationsPageQuery
	setQuery: MyReservationsPageSetQuery
}

const PastReservations = (props: Props) => {
	const { formatMessage } = useIntl()
	const { query, setQuery, pastReservationsContainerRef } = props

	const { locale } = useIntl()
	const { messages } = useMessages()
	const { apiBrowser } = useContext(AppContext)
	const { myLocation } = useContext(MyLocationContext)

	const [pastReservations, setPastReservations] = useState<PastReservationsResponse | undefined>()
	const [isLoading, setIsLoading] = useState(true)
	const [isFetchingFromLoadMoreButton, setIsFetchingFromLoadMoreButton] = useState(false)
	const [refetchPastReservations, setRefetchPastReservations] = useState(false)
	const mergeWithPreviousPastReservations = useRef(false)

	useEffect(() => {
		const fetchPastReservationsData = async () => {
			try {
				setIsLoading(true)
				const pastReservationsResponse = await apiBrowser.b2c.getPastReservations({
					page: query.pastReservationsPage,
					limit: PAST_RESERVATIONS_LIMIT_MY_RESERVATIONS_PAGE
				})

				if (mergeWithPreviousPastReservations.current) {
					setPastReservations((prev) =>
						prev
							? {
									...prev,
									reservations: [...prev.reservations, ...pastReservationsResponse.reservations],
									pagination: pastReservationsResponse.pagination
								}
							: prev
					)
				} else {
					setPastReservations(pastReservationsResponse)
				}
			} catch {
				setPastReservations(undefined)
			} finally {
				setIsLoading(false)
				setIsFetchingFromLoadMoreButton(false)
				mergeWithPreviousPastReservations.current = false
			}
		}

		fetchPastReservationsData()
	}, [apiBrowser, query.pastReservationsPage, refetchPastReservations])

	const fetchPastReservationsFromLoadMoreBtn = () => {
		setIsFetchingFromLoadMoreButton(true)

		// set mergeWithPreviousPastReservations flag to true so the currently showed past reservations can be merged with newly loaded past reservations
		mergeWithPreviousPastReservations.current = true

		setQuery({
			...query,
			pastReservationsPage: query.pastReservationsPage + 1
		})
	}

	const handlePastReservationsPageChange = (newPage: number) => {
		pastReservationsContainerRef.current?.scrollIntoView({ behavior: 'smooth' })
		setQuery({ ...query, pastReservationsPage: newPage })
	}

	const handleRetryFetchingPastReservations = () => {
		if (query.pastReservationsPage !== 1) {
			setQuery({ ...query, pastReservationsPage: 1 })
		} else {
			setRefetchPastReservations((current) => !current)
		}
	}

	const onExploreSalonsClick = () => {
		window.location.href = PAGE_LINKS['/salons/all'](locale, { ...myLocation })
	}

	const remainingPastReservationsCount = pastReservations
		? pastReservations.pagination.totalCount - pastReservations.pagination.page * pastReservations.pagination.limit
		: 0

	const showMorePastReservationsButtonText = formatMessage(
		{ id: 'Show more reservations ({remainingReservationsCount})', defaultMessage: 'Show more reservations ({remainingReservationsCount})' },
		{ remainingReservationsCount: remainingPastReservationsCount }
	)

	if (isLoading && !isFetchingFromLoadMoreButton) {
		return (
			<SC.PastReservationsGrid>
				{Array.from({ length: 12 }, (_, index) => {
					return <ReservationCardSkeleton key={index} cardStyle={'past'} />
				})}
			</SC.PastReservationsGrid>
		)
	}

	if (typeof pastReservations === 'undefined') {
		return <ErrorScreen button={{ onClick: handleRetryFetchingPastReservations }} />
	}

	if (!pastReservations.reservations.length) {
		return (
			<Empty
				title={messages['You don’t have any past reservations']}
				icon={<EmptyCalendarIcon />}
				button={{ label: messages['Explore salons'], onClick: onExploreSalonsClick }}
			/>
		)
	}

	return (
		<>
			<SC.PastReservationsGrid>
				{pastReservations.reservations.map((reservationData) => {
					return (
						<ReservationCard
							key={reservationData.id}
							reservationData={reservationData}
							cardStyle={'past'}
							href={PAGE_LINKS['/salons/my-reservations/:reservationID'](locale, reservationData.id)}
						/>
					)
				})}
			</SC.PastReservationsGrid>
			{pastReservations.reservations.length > 0 && pastReservations.pagination.totalPages > 1 && (
				<SC.BottomNavigation>
					{pastReservations.reservations.length > 0 && query.pastReservationsPage !== pastReservations.pagination.totalPages && (
						<ActionButton
							isLoading={isFetchingFromLoadMoreButton}
							buttonStyle={ButtonModel.Styles.secondary}
							onClick={fetchPastReservationsFromLoadMoreBtn}
						>
							{showMorePastReservationsButtonText}
						</ActionButton>
					)}

					<SC.PaginationWrapper>
						<Pager
							currentPage={query.pastReservationsPage}
							lastPage={pastReservations.pagination.totalPages}
							onPageChange={handlePastReservationsPageChange}
						/>
					</SC.PaginationWrapper>
				</SC.BottomNavigation>
			)}
		</>
	)
}

export default PastReservations
